import { Capacitor, registerPlugin } from "@capacitor/core";

// Use registerPlugin directly to ensure Capacitor 8 compatibility
// instead of relying on the @codetrix-studio/capacitor-google-auth JS proxy
const NativeGoogleAuth = registerPlugin("GoogleAuth");

const SCOPES = "https://www.googleapis.com/auth/drive.appdata";
const EXPIRY_KEY = "accessTokenExpiry"; // epoch ms
// Refresh a bit early so an in-flight request never races the expiry.
const EXPIRY_SKEW_MS = 60_000;

// ── Native: a session this device can't renew ───────────────────────────────
// The refresh token stays in this device's Keystore/Keychain, but userData (the
// signed-in state) is in localStorage, which backups and device transfers restore,
// and sessions signed in before the app kept the token have none. Without one, or
// once Google rejects it (invalid_grant), only a new sign-in brings sync back.
const SIGN_IN_AGAIN = /no refresh token|invalid_grant|expired or revoked/i;
let signInRequired = false;
export const isSignInRequired = () => signInRequired;

// ── Web only: initialise the GAPI Drive client ──────────────────────────────
export const initGapiClient = () => {
  if (Capacitor.isNativePlatform()) return Promise.resolve();
  return new Promise((resolve, reject) => {
    window.gapi.load("client", async () => {
      try {
        await window.gapi.client.init({
          apiKey: process.env.REACT_APP_GOOGLE_API_KEY,
          discoveryDocs: [
            "https://www.googleapis.com/discovery/v1/apis/drive/v3/rest",
          ],
        });
        resolve();
      } catch (err) {
        console.error("GAPI client init failed", err);
        reject(err);
      }
    });
  });
};

// ── Token persistence ─────────────────────────────────────────────────────────
const storeToken = (accessToken, expiresInSec) => {
  if (!accessToken) return;
  signInRequired = false;
  sessionStorage.setItem("accessToken", accessToken);
  if (expiresInSec) {
    sessionStorage.setItem(EXPIRY_KEY, String(Date.now() + expiresInSec * 1000));
  }
  // Keep the copy in userData current too: it's what getAccessToken() falls back to
  // after an app restart, so a refreshed token shouldn't be replaced by a dead one.
  try {
    const userData = JSON.parse(localStorage.getItem("userData") || "null");
    if (userData && userData.accessToken !== accessToken) {
      localStorage.setItem("userData", JSON.stringify({ ...userData, accessToken }));
    }
  } catch {
    /* storage unavailable */
  }
};

// ── Sign in ──────────────────────────────────────────────────────────────────
// Signing in while signed in (again, after a native session this device couldn't
// renew) has to be the same Google account: the data on this device, and what it
// knows of that account's Drive, would otherwise sync into another account's Drive.
// Switching accounts means signing out first, which clears them. userData saved by
// older versions may have no id, and then the email decides.
export class OtherAccountError extends Error {
  constructor(email) {
    super("Signed in with another Google account");
    this.name = "OtherAccountError";
    this.email = email;
  }
}

const otherAccountError = (user) => {
  let current = null;
  try {
    current = JSON.parse(localStorage.getItem("userData") || "null");
  } catch {
    /* storage unavailable */
  }
  if (!current) return null;
  const id = current.id || current.sub;
  const other =
    id && user.id
      ? id !== user.id
      : Boolean(current.email && user.email) && current.email.toLowerCase() !== user.email.toLowerCase();
  return other ? new OtherAccountError(current.email) : null;
};

let tokenClient = null;

const initTokenClient = () => {
  tokenClient = window.google.accounts.oauth2.initTokenClient({
    client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    scope: SCOPES,
    callback: (tokenResponse) => {
      if (tokenResponse.error) return;
      storeToken(tokenResponse.access_token, tokenResponse.expires_in);
    },
  });
};

export const signInWithGoogle = async (onSuccess) => {
  // ── Native (iOS / Android) ────────────────────────────────────────────────
  if (Capacitor.isNativePlatform()) {
    const user = await NativeGoogleAuth.signIn();
    const accessToken = user.authentication.accessToken;
    const userInfo = {
      id: user.id, // Google's stable account id ("sub")
      name: user.name,
      email: user.email,
      picture: user.imageUrl,
      accessToken,
    };
    const otherAccount = otherAccountError(userInfo);
    if (otherAccount) {
      // Its access token is never stored, and signOut drops the refresh token the plugin kept.
      await NativeGoogleAuth.signOut().catch(() => {});
      throw otherAccount;
    }
    storeToken(accessToken, user.authentication?.expiresIn);
    if (onSuccess) onSuccess(userInfo);
    return userInfo;
  }

  // ── Web (popup OAuth) ─────────────────────────────────────────────────────
  return new Promise((resolve, reject) => {
    if (!tokenClient) initTokenClient();

    tokenClient.callback = async (tokenResponse) => {
      if (tokenResponse.error || !tokenResponse.access_token) {
        reject(new Error("Access token not received"));
        return;
      }
      const accessToken = tokenResponse.access_token;
      const userInfoRes = await fetch(
        "https://www.googleapis.com/oauth2/v3/userinfo",
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const userInfo = await userInfoRes.json();
      const fullUser = { ...userInfo, id: userInfo.sub, accessToken };
      const otherAccount = otherAccountError(fullUser);
      if (otherAccount) {
        reject(otherAccount);
        return;
      }
      storeToken(accessToken, tokenResponse.expires_in);
      if (onSuccess) onSuccess(fullUser);
      resolve(fullUser);
    };

    tokenClient.requestAccessToken({ prompt: "consent" });
  });
};

// ── Token access ─────────────────────────────────────────────────────────────
export const getAccessToken = () => {
  // Prefer the sessionStorage token (freshest); fall back to the one stored
  // with userData in localStorage so Drive calls work after an app restart.
  const token =
    sessionStorage.getItem("accessToken") ||
    JSON.parse(localStorage.getItem("userData") || "null")?.accessToken ||
    null;
  if (!token) console.warn("Access token missing — sign in first");
  return token;
};

export const isTokenExpired = () => {
  const raw = sessionStorage.getItem(EXPIRY_KEY);
  if (!raw) return false; // unknown expiry — assume valid, let a 401 trigger refresh
  return Date.now() >= Number(raw) - EXPIRY_SKEW_MS;
};

// ── Silent token refresh ──────────────────────────────────────────────────────
// De-duplicated: concurrent callers share one in-flight refresh.
let refreshPromise = null;

const doRefresh = async () => {
  // ── Native ────────────────────────────────────────────────────────────────
  if (Capacitor.isNativePlatform()) {
    // Only a new sign-in can renew the session now, and until that sign-in has passed
    // the account check, the refresh token the plugin holds may be another account's.
    if (signInRequired) return null;
    try {
      const res = await NativeGoogleAuth.refresh();
      const accessToken = res?.accessToken || res?.authentication?.accessToken;
      if (accessToken) {
        storeToken(accessToken, res?.expiresIn || res?.authentication?.expiresIn);
        return accessToken;
      }
    } catch (err) {
      console.warn("Native token refresh failed", err);
      if (SIGN_IN_AGAIN.test(err?.message || "")) signInRequired = true;
    }
    return null;
  }

  // ── Web (silent re-request, no consent prompt) ──────────────────────────────
  return new Promise((resolve) => {
    let settled = false;
    const finish = (token) => {
      if (settled) return;
      settled = true;
      resolve(token);
    };
    try {
      if (!tokenClient) initTokenClient();
      tokenClient.callback = (tokenResponse) => {
        if (tokenResponse.error || !tokenResponse.access_token) {
          finish(null);
          return;
        }
        storeToken(tokenResponse.access_token, tokenResponse.expires_in);
        finish(tokenResponse.access_token);
      };
      // GIS surfaces popup/timeout/network problems here, not via callback.
      tokenClient.error_callback = () => finish(null);
      // Watchdog: if GIS never fires either callback, don't hang forever.
      setTimeout(() => finish(null), 15000);
      // Empty prompt → silent if the user already granted consent.
      tokenClient.requestAccessToken({ prompt: "" });
    } catch (err) {
      console.warn("Web token refresh failed", err);
      finish(null);
    }
  });
};

export const refreshAccessToken = () => {
  if (refreshPromise) return refreshPromise;
  // Belt-and-suspenders: never let a stuck refresh poison future callers.
  refreshPromise = Promise.race([
    doRefresh(),
    new Promise((resolve) => setTimeout(() => resolve(null), 20000)),
  ]).finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
};

// ── Sign out ─────────────────────────────────────────────────────────────────
export const signOutGoogle = async () => {
  signInRequired = false;
  if (Capacitor.isNativePlatform()) {
    try {
      await NativeGoogleAuth.signOut();
    } catch (_) {}
  } else {
    window.google?.accounts.id?.disableAutoSelect();
  }
  sessionStorage.removeItem("accessToken");
  sessionStorage.removeItem(EXPIRY_KEY);
};
